/**
 * Plan checks (TD §8, FD §4), ported from the prototype's analyse(): space and water per zone, which roads reach
 * the entrance, which zones a reachable road runs along, overlaps, the zoo boundary, and bridges over rivers.
 * Messages are plain English for the Selected card, e.g. "African lion habitat is 300 m² too small".
 */
import { speciesNeeds } from '../../shared/species-needs';
import type { AnimalArea, Analysis, AreaId, HabitatStats, Issue, PlanDoc, Pt, RoadId, Species } from '../../shared/types';
import { bbox, clamp, distPoly, lerpAt, nearestOnPoly, pip, polyArea, polyLen, segX, type Derived } from '../geom';
import { isAnimalArea } from '../model';

/** A zone is too small under this share of the space it needs. */
export const AREA_OK_SHARE = 0.97;
/** A zone lacks water under this share of the water it needs. */
export const WATER_OK_SHARE = 0.9;
/** A road surface within this distance (m) of a zone counts as running along it. */
export const REACH_M = 6;
/** Two zones overlap when sample points lie deeper than this (m) inside the other, … */
export const OVERLAP_DEPTH_M = 4;
/** … for more than this share of the zone. */
export const OVERLAP_SHARE = 0.03;
/** A zone sticks out when more than this share lies outside the boundary. */
export const OUTSIDE_SHARE = 0.04;
/** Roads within this distance (m) of the entrance icon reach the entrance. */
export const ENTRANCE_REACH_M = 25;

type Box = [number, number, number, number];

/** Sorted x positions where a horizontal line at y crosses the ring (the same even-odd rule as pip). */
function crossings(ring: readonly Pt[], y: number): number[] {
  const xs: number[] = [], n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = ring[i] as Pt, [xj, yj] = ring[j] as Pt;
    if ((yi > y) !== (yj > y)) xs.push((xj - xi) * (y - yi) / (yj - yi) + xi);
  }
  return xs.sort((a, b) => a - b);
}

/** True when x lies inside, given the row's sorted crossings. */
function insideRow(xs: readonly number[], x: number): boolean {
  let c = 0;
  for (const v of xs) { if (v > x) c++; }
  return (c & 1) === 1;
}
const fmt = (n: number) => Math.round(n).toLocaleString('en-US');
const boxesMeet = (a: Box, b: Box, pad = 0) => a[0] - pad <= b[2] && b[0] - pad <= a[2] && a[1] - pad <= b[3] && b[1] - pad <= a[3];
const inBox = (b: Box, x: number, y: number, pad = 0) => x >= b[0] - pad && x <= b[2] + pad && y >= b[1] - pad && y <= b[3] + pad;

interface RoadSample { x: number; y: number; w: number; road: number }

/** Plain name of a zone: "African lion habitat", "Toco toucan aviary". */
export function zoneName(a: AnimalArea, sp: Species | undefined): string {
  return `${sp ? sp.name : 'Unknown animal'} ${a.kind}`;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Checks a plan. `derived` must be derive(doc); `extraSpacePct` is the plan's extra space (normally
 * doc.settings.extraSpacePct). Runs in a few milliseconds for typical plans.
 */
export function analysePlan(doc: PlanDoc, derived: Derived, species: Species[], extraSpacePct: number): Analysis {
  const byId = new Map(species.map(s => [s.id, s])), issues: Issue[] = [];

  // roads: samples every 8 m along the centre line, with the local width
  const roadIds = Object.keys(doc.roads) as RoadId[];
  const polys = roadIds.map(id => derived.roadPolys[id] ?? { pts: [] as Pt[], widths: [] as number[] });
  const samples: RoadSample[] = [], byRoad: RoadSample[][] = roadIds.map(() => []);
  polys.forEach((p, ri) => {
    const pl = p.pts;
    for (let i = 0; i < pl.length - 1; i++) {
      const a = pl[i] as Pt, b = pl[i + 1] as Pt, l = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (!l) continue;
      const w = ((p.widths[i] ?? 0) + (p.widths[i + 1] ?? 0)) / 2;
      for (let d = 0; d < l; d += 8) (byRoad[ri] as RoadSample[]).push({ x: a[0] + (b[0] - a[0]) * d / l, y: a[1] + (b[1] - a[1]) * d / l, w, road: ri });
    }
    const q = pl[pl.length - 1];
    if (q) (byRoad[ri] as RoadSample[]).push({ x: q[0], y: q[1], w: p.widths[pl.length - 1] ?? 0, road: ri });
    samples.push(...(byRoad[ri] as RoadSample[]));
  });
  const roadBox: Box[] = polys.map(p => (p.pts.length ? bbox(p.pts) : [1e9, 1e9, -1e9, -1e9]));
  const roadW = polys.map(p => (p.widths.length ? Math.max(...p.widths) : 0));

  // road network: joined where they share a dot or their surfaces touch
  const par = roadIds.map((_, i) => i), find = (i: number): number => (par[i] === i ? i : (par[i] = find(par[i] as number)));
  const nodeRoad = new Map<string, number>();
  roadIds.forEach((id, i) => {
    for (const n of doc.roads[id]?.nodes ?? []) {
      const o = nodeRoad.get(n);
      if (o === undefined) nodeRoad.set(n, i); else par[find(i)] = find(o);
    }
  });
  const touch = (S: RoadSample[], q: number) => {
    const qp = polys[q] as { pts: Pt[]; widths: number[] };
    return S.some(s => {
      if (!inBox(roadBox[q] as Box, s.x, s.y, (s.w + (roadW[q] as number)) / 2)) return false;
      const h = nearestOnPoly(qp.pts, s.x, s.y);
      return h.d <= (s.w + lerpAt(qp.widths, h.i, h.t)) / 2;
    });
  };
  for (let a = 0; a < roadIds.length; a++) for (let b = a + 1; b < roadIds.length; b++) {
    if (find(a) === find(b)) continue;
    const pad = ((roadW[a] as number) + (roadW[b] as number)) / 2;
    if (!boxesMeet(roadBox[a] as Box, roadBox[b] as Box, pad)) continue;
    if (touch(byRoad[a] as RoadSample[], b) || touch(byRoad[b] as RoadSample[], a)) par[find(a)] = find(b);
  }
  const ent = Object.values(doc.icons).find(f => f.type === 'entrance');
  const plazas = Object.keys(doc.areas).filter(id => doc.areas[id]?.kind === 'plaza').map(id => derived.areaRings[id] ?? []).filter(r => r.length >= 3);
  const hub = new Set<number>();
  roadIds.forEach((_, i) => {
    if ((byRoad[i] as RoadSample[]).some(s => (ent && Math.hypot(s.x - ent.x, s.y - ent.y) < ENTRANCE_REACH_M) || plazas.some(pl => pip(pl, s.x, s.y)))) hub.add(find(i));
  });
  const noHub = !ent && !plazas.length;
  const roads: Analysis['roads'] = {};
  roadIds.forEach((id, i) => {
    const connected = noHub || hub.has(find(i));
    roads[id] = { length: polyLen((polys[i] as { pts: Pt[] }).pts), connected };
    if (!connected) issues.push({ targetId: id, severity: 'warn', message: "This path isn't connected to the entrance" });
  });
  const good = samples.filter(s => roads[roadIds[s.road] as RoadId]?.connected);

  // bridges where a road crosses a river
  const bridges: Pt[] = [];
  for (const rv of Object.values(derived.riverPolys)) {
    if (rv.length < 2) continue;
    const rb = bbox(rv);
    polys.forEach((p, ri) => {
      if (!boxesMeet(roadBox[ri] as Box, rb)) return;
      for (let i = 0; i < p.pts.length - 1; i++) for (let j = 0; j < rv.length - 1; j++) {
        const x = segX(p.pts[i] as Pt, p.pts[i + 1] as Pt, rv[j] as Pt, rv[j + 1] as Pt);
        if (x && !bridges.some(b => Math.hypot(b[0] - x[0], b[1] - x[1]) < 15)) bridges.push(x);
      }
    });
  }

  // water: lakes, pools and rivers
  const waterAreas = Object.keys(doc.areas).filter(id => { const k = doc.areas[id]?.kind; return k === 'lake' || k === 'pool'; })
    .map(id => derived.areaRings[id] ?? []).filter(r => r.length >= 3).map(r => ({ r, b: bbox(r) }));
  const rivers = Object.entries(doc.rivers).map(([id, rv]) => ({ p: derived.riverPolys[id] ?? [], w: rv.w })).filter(x => x.p.length >= 2).map(x => ({ ...x, b: bbox(x.p) }));

  // zones
  const plot = derived.plotRing && derived.plotRing.length >= 3 ? derived.plotRing : null;
  const zones = Object.entries(doc.areas).filter((e): e is [AreaId, AnimalArea] => isAnimalArea(e[1]))
    .map(([id, a]) => { const ring = derived.areaRings[id] ?? []; return { id, a, ring, b: (ring.length ? bbox(ring) : [0, 0, 0, 0]) as Box }; });
  const habitats: Record<AreaId, HabitatStats> = {};
  for (const z of zones) {
    const sp = byId.get(z.a.speciesId), name = zoneName(z.a, sp), need = sp ? speciesNeeds(sp, z.a.count, extraSpacePct) : { area: 0, water: 0 };
    const stats: HabitatStats = { area: 0, need: need.area, water: 0, needWater: need.water, reachable: false };
    habitats[z.id] = stats;
    if (!sp) issues.push({ targetId: z.id, severity: 'warn', message: `This ${z.a.kind}'s animal isn't in the species list, so its size can't be checked` });
    if (z.ring.length < 3) continue;
    const area = polyArea(z.ring), st = clamp(Math.sqrt(area) / 26, 1.5, 6), [x0, y0, x1, y1] = z.b;
    const others = zones.filter(o => o !== z && o.ring.length >= 3 && boxesMeet(o.b, z.b));
    const ovCount = new Map<AreaId, number>();
    let nIn = 0, nW = 0, nOut = 0;
    // sample on a grid of step st, row by row: each ring is crossed once per row instead of once per sample
    for (let y = y0 + st / 2; y < y1; y += st) {
      const zx = crossings(z.ring, y);
      if (zx.length < 2) continue;
      const px = plot ? crossings(plot, y) : null;
      const wx = waterAreas.filter(w => y >= w.b[1] && y <= w.b[3]).map(w => crossings(w.r, y));
      const rv = rivers.filter(r => y >= r.b[1] - r.w / 2 && y <= r.b[3] + r.w / 2);
      const ox = others.filter(o => y >= o.b[1] && y <= o.b[3]).map(o => ({ o, xs: crossings(o.ring, y) }));
      for (let x = x0 + st / 2; x < x1; x += st) {
        if (!insideRow(zx, x)) continue;
        nIn++;
        if (wx.some(xs => insideRow(xs, x)) || rv.some(r => x >= r.b[0] - r.w / 2 && x <= r.b[2] + r.w / 2 && distPoly(r.p, x, y, false) <= r.w / 2)) nW++;
        for (const { o, xs } of ox) if (insideRow(xs, x) && distPoly(o.ring, x, y, true) > OVERLAP_DEPTH_M) ovCount.set(o.id, (ovCount.get(o.id) ?? 0) + 1);
        if (px && !insideRow(px, x)) nOut++;
      }
    }
    stats.area = area;
    stats.water = nW * area / Math.max(nIn, 1);
    const along = (S: RoadSample[]) => S.some(s => inBox(z.b, s.x, s.y, 15) && (pip(z.ring, s.x, s.y) || distPoly(z.ring, s.x, s.y, true) < s.w / 2 + REACH_M));
    const touches = along(samples);
    stats.reachable = touches && along(good);
    if (sp && area < need.area * AREA_OK_SHARE) issues.push({ targetId: z.id, severity: 'bad', message: `${cap(name)} is ${fmt(need.area - area)} m² too small` });
    if (sp && need.water > 0 && stats.water < need.water * WATER_OK_SHARE) issues.push({ targetId: z.id, severity: 'warn', message: `${cap(name)} needs ${fmt(need.water - stats.water)} m² more water` });
    if (!touches) issues.push({ targetId: z.id, severity: 'bad', message: `${cap(name)} has no path along it` });
    else if (!stats.reachable) issues.push({ targetId: z.id, severity: 'bad', message: `The path by the ${name} doesn't connect to the entrance` });
    let worst: AreaId | null = null, wc = 0;
    for (const [oid, c] of ovCount) if (c > wc) { wc = c; worst = oid; }
    if (worst && wc > nIn * OVERLAP_SHARE) {
      const o = zones.find(q => q.id === worst) as (typeof zones)[number];
      issues.push({ targetId: z.id, severity: 'bad', message: `${cap(name)} overlaps the ${zoneName(o.a, byId.get(o.a.speciesId))}` });
    }
    if (nOut > nIn * OUTSIDE_SHARE) issues.push({ targetId: z.id, severity: 'bad', message: `${cap(name)} sticks out of the zoo boundary` });
  }
  return { issues, habitats, roads, bridges };
}
